// spike-audit-ignore: GRAPHQL_NO_ENV -- Amplify configured via aws-exports.js which is environment-specific per Amplify build

import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.jsx'
import { BrowserRouter } from 'react-router-dom'
import { Amplify } from 'aws-amplify'
import { registerSW } from 'virtual:pwa-register'
import config from './aws-exports.js'
import './globals.css'
import './styleguide.css'
import './index.css'
import './framework.css'
import 'react-datepicker/dist/react-datepicker.css';
import { SetS3Config } from './services';
SetS3Config(import.meta.env.VITE_S3_UNIT_IMAGES_BUCKET, 'protected');

// console.log('Loading AWS config:', config.oauth.domain);

Amplify.configure(config)

// ---------------------------------------------------------------------------
// PWA service-worker registration with an explicit update prompt.
//
// With registerType: 'prompt' (and skipWaiting/clientsClaim removed), a new
// build does NOT silently take over a running session. Instead onNeedRefresh
// fires, we decide whether the user actually needs to act, and if so set
// window.__pwaNeedRefresh and broadcast 'pwa:need-refresh' so the UpdateBanner
// and VersionStatus show a Reload button.
//
// Aug 2026 -- two failure modes fixed here:
//
//   1. NO UPDATE CHECK. The browser only fetches a new sw.js on a navigation
//      or an explicit registration.update(). Fixed by holding the
//      registration from onRegisteredSW and calling update() on an interval
//      and on visibilitychange.
//
//   2. UNCONTROLLED PAGE. updateSW(true) waits for 'controllerchange', which
//      never fires on an uncontrolled page (the state left after self-heal,
//      since clientsClaim is off). Fixed by detecting controller === null and
//      handling activation + reload ourselves, plus a hard fallback timeout.
//
// Sep 2026 -- three more:
//
//   3. PREMATURE RELOAD on the uncontrolled path. It posted SKIP_WAITING and
//      reloaded immediately; the reload could win the race, the new page
//      still saw a waiting worker, and the banner came right back. It now
//      waits for the worker to reach 'activated' (bounded by a timeout).
//
//   4. BANNER WITH NO CODE UPDATE. Navigations and same-origin assets are
//      NetworkFirst (see vite.config), so a page can already be running the
//      live build while an older worker is still active and the new one sits
//      in waiting. Users then saw "New version available" next to a green
//      "Up to date" at the same hash. onNeedRefresh now checks version.json:
//      if the running commit already matches live, nothing is shown. The
//      waiting worker activates once all tabs close, or is replaced by the
//      next real deploy (which does surface the prompt). We deliberately do
//      NOT post SKIP_WAITING here: vite-plugin-pwa reloads the page on
//      'controlling', which would yank a user mid-task for no code change.
//
//   5. ONE-SHOT EVENT. 'pwa:need-refresh' was only heard by components that
//      happened to be mounted when it fired, so the banner and the status dot
//      could disagree. The state now lives on window.__pwaNeedRefresh and
//      components read it on mount as well as listening for the event.
// ---------------------------------------------------------------------------

// How often to ask the browser to re-fetch sw.js. Overridden to 60000 on
// devv4 via VITE_SW_UPDATE_INTERVAL_MS; prod inherits the hourly default.
const SW_UPDATE_INTERVAL_MS =
  Number(import.meta.env.VITE_SW_UPDATE_INTERVAL_MS) || 60 * 60 * 1000

// If controllerchange hasn't reloaded us by now, reload anyway.
const RELOAD_FALLBACK_MS = 3000

// How long to wait for a waiting worker to activate on the uncontrolled path.
const ACTIVATE_TIMEOUT_MS = 3000

const RUNNING_COMMIT = import.meta.env.VITE_COMMIT_HASH || 'unknown'

let swRegistration = null

// Persistent "user needs to reload" flag. Components read this on mount so a
// late-mounting one doesn't miss an event that already fired.
window.__pwaNeedRefresh = false

function surfaceUpdate(reason) {
  if (window.__pwaNeedRefresh) return
  console.info('[pwa] showing update prompt:', reason)
  window.__pwaNeedRefresh = true
  window.dispatchEvent(new CustomEvent('pwa:need-refresh'))
}

async function fetchLiveCommit() {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
    if (!res.ok) return null
    const data = await res.json()
    return data?.commit ?? null
  } catch {
    return null
  }
}

// A worker is waiting. Only bother the user if their code is actually behind.
async function handleNeedRefresh() {
  const live = await fetchLiveCommit()

  if (!live) {
    // Can't verify -- err on the side of telling them.
    surfaceUpdate('waiting worker; could not verify live build')
    return
  }

  if (live !== RUNNING_COMMIT) {
    surfaceUpdate(`running ${RUNNING_COMMIT}, live ${live}`)
    return
  }

  console.info(
    `[pwa] worker waiting but code is current (${RUNNING_COMMIT}); not prompting`
  )
}

const updateSW = registerSW({
  onRegisteredSW(swUrl, registration) {
    swRegistration = registration
    if (!registration) return

    // Periodic check. Without this, a client that never navigates never
    // discovers a new worker.
    setInterval(() => {
      if (!navigator.onLine) return
      registration.update().catch(() => {})
    }, SW_UPDATE_INTERVAL_MS)

    // Also check when the tab comes back to the foreground -- covers the
    // sleep/resume case immediately instead of waiting out the interval,
    // and covers background tabs whose timers the browser has throttled.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        registration.update().catch(() => {})
      }
    })
  },
  onNeedRefresh() {
    handleNeedRefresh()
  },
  onOfflineReady() {},
})

// Force an update check. VersionStatus calls this when the hash in
// version.json differs from VITE_COMMIT_HASH, so detection actually drives
// application instead of just observing it.
window.__checkForUpdate = async () => {
  try {
    const registration =
      swRegistration || (await navigator.serviceWorker?.getRegistration())
    await registration?.update()
    return true
  } catch {
    return false
  }
}

// Wipe every Cache Storage bucket for this origin. This is the active
// ingredient in recovery -- unregister() alone leaves the current page
// controlled and still served from the precache.
async function purgeCaches() {
  try {
    const keys = await caches.keys()
    await Promise.all(keys.map((k) => caches.delete(k)))
  } catch {
    /* ignore */
  }
}

// Resolve once the worker is activated (or redundant), or after timeoutMs.
function waitForActivation(sw, timeoutMs) {
  return new Promise((resolve) => {
    const settled = () => sw.state === 'activated' || sw.state === 'redundant'
    if (settled()) return resolve(sw.state)
    const timer = setTimeout(() => resolve('timeout'), timeoutMs)
    sw.addEventListener('statechange', () => {
      if (settled()) {
        clearTimeout(timer)
        resolve(sw.state)
      }
    })
  })
}

let applying = false

// Exposed so the banner (or anywhere) can trigger the update + reload on click.
// This function must ALWAYS end in a reload. Every branch either reloads
// directly or arms RELOAD_FALLBACK_MS first.
window.__applyUpdate = async () => {
  if (applying) return
  applying = true
  console.info('[pwa] applyUpdate clicked')

  if (!('serviceWorker' in navigator)) {
    console.info('[pwa] no service worker support; plain reload')
    window.location.reload()
    return
  }

  try {
    let registration = await navigator.serviceWorker.getRegistration()
    console.info('[pwa] state:', {
      controlled: !!navigator.serviceWorker.controller,
      active: registration?.active?.state ?? null,
      waiting: registration?.waiting?.state ?? null,
    })

    // Give the browser a chance to find a new worker before giving up on it.
    if (registration && !registration.waiting) {
      console.info('[pwa] nothing waiting; checking for a new worker')
      await registration.update().catch(() => {})
      await new Promise((resolve) => setTimeout(resolve, 1500))
      registration = await navigator.serviceWorker.getRegistration()
    }

    if (registration?.waiting) {
      // Uncontrolled page: controllerchange will never fire, so updateSW's
      // internal reload never happens. Activate the waiting worker ourselves
      // and WAIT for it before reloading -- otherwise the reload can win the
      // race and the new page still sees a waiting worker.
      if (!navigator.serviceWorker.controller) {
        const sw = registration.waiting
        console.warn('[pwa] page uncontrolled; activating waiting worker before reload')
        sw.postMessage({ type: 'SKIP_WAITING' })
        const result = await waitForActivation(sw, ACTIVATE_TIMEOUT_MS)
        console.info('[pwa] uncontrolled path: worker', result, '- reloading')
        window.location.reload()
        return
      }

      // Normal path. Arm a fallback in case controllerchange never arrives.
      console.info('[pwa] normal path: updateSW(true), fallback armed')
      setTimeout(() => {
        console.warn('[pwa] fallback reload fired: controllerchange never arrived')
        window.location.reload()
      }, RELOAD_FALLBACK_MS)
      updateSW(true)
      return
    }

    // Self-heal: no waiting worker, so this client is pinned. Tear it down.
    console.warn('[pwa] no waiting worker; self-healing registration + caches')
    await registration?.unregister()
    await purgeCaches()
    window.location.reload()
  } catch (err) {
    console.error('[pwa] update failed, forcing reload', err)
    await purgeCaches()
    window.location.reload()
  }
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </React.StrictMode>,
)
